import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Plus, WandSparkles } from 'lucide-react';
import type {
  DocumentPublicationListItem,
  DocumentTemplateCategory,
  DocumentTemplateListItem,
} from '@ip/shared-types';
import { DOCUMENT_TEMPLATE_CATEGORIES } from '@ip/shared-types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { HeaderActions } from '@/components/layout/AppShell';
import type { AppShellContext } from '@/components/layout/AppShell';
import { TemplateList, type CategoryFilter } from '@/components/documents/TemplateList';
import { PublicationList } from '@/components/documents/PublicationList';
import { PublishDialog } from '@/components/documents/PublishDialog';
import { PublicationDetailSheet } from '@/components/documents/PublicationDetailSheet';
import { useDocumentsApi } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { hasCompanyRole } from '@/lib/permissions';
import { t } from '@/i18n';

/**
 * Documents section (Task 5, spec §8.1-8.2): per-company markdown template
 * repository — list + create dialog. Role gate happens server-side
 * (DocumentTemplatesService); the sidebar entry is additionally hidden for
 * non-admin/hr/ksiegowy in AppSidebar, and pure-ksiegowy callers only ever
 * get contractor-category rows back from the list endpoint.
 */
export function DocumentsPage() {
  const { nip } = useOutletContext<AppShellContext>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { permissions, isSuperAdmin } = useAuth();
  const {
    listTemplates, createTemplate, createStarterUmowaZlecenieStudent, archiveTemplate,
    listPublications, archivePublication,
  } = useDocumentsApi();

  // A pure-ksiegowy user (no admin/hr) may only create contractor-category
  // templates — the backend rejects the others. Offer only what will be
  // accepted instead of surfacing a generic error after submit.
  const allowedCategories = useMemo(
    () =>
      isSuperAdmin || hasCompanyRole(permissions, nip, ['admin', 'hr'])
        ? DOCUMENT_TEMPLATE_CATEGORIES
        : (['contractor'] as const),
    [permissions, isSuperAdmin, nip],
  );

  const [tab, setTab] = useState<'templates' | 'publications'>('templates');

  const [templates, setTemplates] = useState<DocumentTemplateListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState<CategoryFilter>('all');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [starterSaving, setStarterSaving] = useState(false);
  const [name, setName] = useState('');
  const [newCategory, setNewCategory] = useState<DocumentTemplateCategory>(allowedCategories[0]);
  const [description, setDescription] = useState('');

  // Starter templates create employee-category templates — same admin/hr gate
  // the backend enforces (a pure-ksiegowy caller would only get a 403).
  const canUseStarter = isSuperAdmin || hasCompanyRole(permissions, nip, ['admin', 'hr']);

  const [publications, setPublications] = useState<DocumentPublicationListItem[]>([]);
  const [publicationsLoading, setPublicationsLoading] = useState(true);
  const [selectedPublicationId, setSelectedPublicationId] = useState<string | null>(null);

  const [publishTarget, setPublishTarget] = useState<DocumentTemplateListItem | null>(null);

  // A ref bumped on each load so a slow earlier response (e.g. rapid category
  // switching) can't overwrite a newer one — the sibling loaders in this
  // feature use the same guard.
  const templatesReq = useRef(0);
  const loadTemplates = useCallback(async () => {
    const seq = ++templatesReq.current;
    try {
      setLoading(true);
      const data = await listTemplates(nip, {
        category: category === 'all' ? undefined : category,
        includeArchived: true,
      });
      if (seq === templatesReq.current) setTemplates(data);
    } catch (error) {
      if (seq === templatesReq.current) {
        toast.error(t('documents.templates.loadError'));
        console.error(error);
      }
    } finally {
      if (seq === templatesReq.current) setLoading(false);
    }
  }, [listTemplates, nip, category]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const openCreate = () => {
    setName('');
    setNewCategory(allowedCategories[0]);
    setDescription('');
    setDialogOpen(true);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error(t('documents.templates.nameRequired'));
      return;
    }
    try {
      setSaving(true);
      const created = await createTemplate(nip, {
        category: newCategory,
        name: name.trim(),
        description: description.trim() || undefined,
      });
      toast.success(t('documents.templates.created'));
      setDialogOpen(false);
      navigate(`/${nip}/documents/templates/${created.id}`);
    } catch (error) {
      toast.error(t('documents.templates.createError'));
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  // Built-in "Umowa zlecenie (student)" starter (umowa spec 2026-07-23 §7):
  // created AND published (v1) server-side, then opened in the editor.
  const handleCreateStarter = async () => {
    try {
      setStarterSaving(true);
      const created = await createStarterUmowaZlecenieStudent(nip);
      toast.success(t('documents.templates.starterCreated'));
      setDialogOpen(false);
      navigate(`/${nip}/documents/templates/${created.id}`);
    } catch (error) {
      // A 409 (template already exists) carries a meaningful server message.
      toast.error(error instanceof Error ? error.message : t('documents.templates.starterError'));
      console.error(error);
    } finally {
      setStarterSaving(false);
    }
  };

  const handleToggleArchive = async (template: DocumentTemplateListItem) => {
    try {
      await archiveTemplate(nip, template.id, !template.archivedAt);
      toast.success(
        template.archivedAt
          ? t('documents.templates.unarchiveSuccess')
          : t('documents.templates.archiveSuccess'),
      );
      await loadTemplates();
    } catch (error) {
      toast.error(t('documents.templates.archiveError'));
      console.error(error);
    }
  };

  const publicationsReq = useRef(0);
  const loadPublications = useCallback(async () => {
    const seq = ++publicationsReq.current;
    try {
      setPublicationsLoading(true);
      const data = await listPublications(nip, { includeArchived: true });
      if (seq === publicationsReq.current) setPublications(data);
    } catch (error) {
      if (seq === publicationsReq.current) {
        toast.error(t('documents.publications.loadError'));
        console.error(error);
      }
    } finally {
      if (seq === publicationsReq.current) setPublicationsLoading(false);
    }
  }, [listPublications, nip]);

  useEffect(() => {
    if (tab === 'publications') loadPublications();
  }, [tab, loadPublications]);

  // Deep link from the "signed document returned" bell notification:
  // ?tab=publications&pub=<id> selects the Publications tab and opens that
  // publication's detail sheet, then clears the params so back/refresh is
  // clean. A pub id that no longer exists just leaves the tab open (the sheet
  // toasts a load error, no crash). Runs once.
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current) return;
    const tabParam = searchParams.get('tab');
    const pubParam = searchParams.get('pub');
    if (tabParam !== 'publications' && !pubParam) return;
    deepLinkHandled.current = true;
    setTab('publications');
    if (pubParam) setSelectedPublicationId(pubParam);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleArchivePublication = async (publication: DocumentPublicationListItem) => {
    try {
      await archivePublication(nip, publication.id);
      toast.success(t('documents.publications.archiveSuccess'));
      await loadPublications();
    } catch (error) {
      toast.error(t('documents.publications.archiveError'));
      console.error(error);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {tab === 'templates' && (
        <HeaderActions>
          <Button size="sm" onClick={openCreate}>
            <Plus className="size-4" />
            {t('documents.templates.newTemplate')}
          </Button>
        </HeaderActions>
      )}

      <Tabs value={tab} onValueChange={(v) => setTab(v as 'templates' | 'publications')}>
        <TabsList>
          <TabsTrigger value="templates">{t('documents.templates.title')}</TabsTrigger>
          <TabsTrigger value="publications">{t('documents.publications.title')}</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="pt-2">
          <TemplateList
            templates={templates}
            loading={loading}
            category={category}
            onCategoryChange={setCategory}
            onRowClick={(template) => navigate(`/${nip}/documents/templates/${template.id}`)}
            onToggleArchive={handleToggleArchive}
            onPublish={setPublishTarget}
          />
        </TabsContent>

        <TabsContent value="publications" className="pt-2">
          <PublicationList
            publications={publications}
            loading={publicationsLoading}
            onRowClick={(publication) => setSelectedPublicationId(publication.id)}
            onArchive={handleArchivePublication}
          />
        </TabsContent>
      </Tabs>

      {publishTarget && (
        <PublishDialog
          nip={nip}
          open={publishTarget !== null}
          onOpenChange={(open) => !open && setPublishTarget(null)}
          template={publishTarget}
          onPublished={() => {
            setPublishTarget(null);
            if (tab === 'publications') loadPublications();
          }}
        />
      )}

      <PublicationDetailSheet
        nip={nip}
        publicationId={selectedPublicationId}
        onOpenChange={(open) => !open && setSelectedPublicationId(null)}
        onChanged={loadPublications}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('documents.templates.newTemplate')}</DialogTitle>
          </DialogHeader>
          {canUseStarter && (
            <div className="flex flex-col gap-2 rounded-md border border-dashed p-3">
              <p className="text-muted-foreground text-xs">{t('documents.templates.starterHint')}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={saving || starterSaving}
                onClick={handleCreateStarter}
              >
                {starterSaving ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <WandSparkles className="size-4" />
                )}
                {t('documents.templates.starterUmowa')}
              </Button>
            </div>
          )}
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              handleCreate();
            }}
          >
            <div className="space-y-1">
              <Label htmlFor="template-name" className="text-muted-foreground text-xs">
                {t('documents.templates.name')}
              </Label>
              <Input
                id="template-name"
                placeholder={t('documents.templates.namePlaceholder')}
                value={name}
                maxLength={200}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="template-category" className="text-muted-foreground text-xs">
                {t('documents.templates.category')}
              </Label>
              <Select
                value={newCategory}
                onValueChange={(value) => setNewCategory(value as DocumentTemplateCategory)}
              >
                <SelectTrigger id="template-category" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {allowedCategories.map((c) => (
                    <SelectItem key={c} value={c}>
                      {t(`documents.templates.categories.${c}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="template-description" className="text-muted-foreground text-xs">
                {t('documents.templates.description')}
              </Label>
              <Textarea
                id="template-description"
                placeholder={t('documents.templates.descriptionPlaceholder')}
                value={description}
                rows={3}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                disabled={saving}
                onClick={() => setDialogOpen(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="size-4 animate-spin" />}
                {t('common.add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
